<template>
  <div class="satellites">
    <v-container class="my-5">
      <v-row class="ml-1">
        <v-col cols="12" sm="6" md="3" lg="3" class="mt-1">
          <v-text-field v-model="findTxt" hide-details outlined dense clearable label="Find" prepend-inner-icon="mdi-magnify"></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3" lg="2">
          <v-select v-model="statusSelect" :items="statusItems" label="Status" multiple chips hide-details clearable dense prepend-inner-icon="mdi-filter-menu" :menu-props="{ bottom: true, offsetY: true }">
            <template v-slot:selection="data">
              <v-chip :class="data.item" :key="JSON.stringify(data.item)" v-bind="data.attrs" :input-value="data.selected" @click:close="statusSelect.splice(statusSelect.indexOf(data.item), 1)" close >
                {{ data.item }}
              </v-chip>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3" lg="2">
          <v-select v-model="freqSelect" :items="freqItems" label="Frequency" multiple hide-details chips clearable dense prepend-inner-icon="mdi-radio-tower" :menu-props="{ bottom: true, offsetY: true }">
            <template v-slot:selection="data">
              <v-chip :key="JSON.stringify(data.item)" v-bind="data.attrs" :input-value="data.selected" @click:close="freqSelect.splice(freqSelect.indexOf(data.item), 1)" close >
                {{ data.item }}
              </v-chip>
            </template>
          </v-select>
        </v-col>
         <v-col cols="12" sm="6" md="3" lg="2">
          <v-select
            v-model="modeSelect"
            :items="modeItems"
            label="Mode"
            multiple
            hide-details
            chips
            clearable
            dense
            prepend-inner-icon="mdi-tune"
            :menu-props="{ bottom: true, offsetY: true }"
          >
            <template v-slot:selection="data">
              <v-chip
                :key="JSON.stringify(data.item)"
                v-bind="data.attrs"
                :input-value="data.selected"
                @click:close="modeSelect.splice(modeSelect.indexOf(data.item), 1)"
                close
              >
                {{ data.item }}
              </v-chip>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-layout row wrap>
        <v-flex xs12 sm6 md4 lg3 v-for="sat in satellites" :key="sat.name">
          <v-card class="text-center ma-4 rounded-lg clickable" :to="`satellite/${sat.name}`">
            <v-img height="300" :src="sat.images[0]">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey"></v-progress-circular>
                </v-row>
              </template>
            </v-img>

            <v-row align="center" justify="end">
              <v-icon class="mr-1">mdi-satellite-variant</v-icon>
              <span class="subheading mr-2">{{sat.confirmedPackets ? sat.confirmedPackets.toLocaleString() : 0}}</span>
              <span class="mr-1">·</span>
              <v-icon class="mr-1">mdi-radio-tower</v-icon>
              <span class="subheading">{{sat.frames ? sat.frames.toLocaleString() : 0}}</span>
            </v-row>

            <v-card-text style="overflow-y: auto; height:150px">
              <div class="subheading"><strong>{{ sat.displayName }}</strong></div>
              <div class="grey--text">{{ sat.description }}</div>
            </v-card-text>
            <v-card-text>
              <div class="mr-1" v-if="sat.lastPacketTime"><strong>Last Packet:</strong> {{ formatLaunchDate(sat.lastPacketTime) }}</div>
              <div class="mt-3" v-if="sat.status=='Future'"><strong>Launch Date:</strong> {{formatLaunchDate(sat.launchDate)}}</div>
            </v-card-text>

            <v-chip v-for="(conf,i) in sat.configurations" :key="i" :class="`${conf.mode} ma-2`">{{conf.mode}}@{{conf.freq}}</v-chip>
            <v-chip :class="`${sat.status} ma-2`">{{sat.status}}</v-chip>

            <v-card-actions>
              </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
       <v-container v-if="satellites && satellites.length === 0" class="text-center grey--text mt-5">
          No satellites match the current filters.
      </v-container>
    </v-container>
  </div>
</template>

<script>
const axios = require("axios");
import moment from 'moment'; // moment/moment -> moment if using default export

export default {
  name: 'Satellites',
  data() {
    return {
      findTxt: "",
      statusSelect: ['Supported'], // Default status filters
      statusItems: ['Supported', 'Future', 'Experimental', 'Inactive', ], // Added SupportedSB if it's a distinct status
      freqSelect: ['433'], // Default frequency filters (e.g., ['433']) - now empty to show all
      freqItems: ['137','433','868-915','2400'], // Frequency band labels
      
      // *** NEW Data properties for Mode filter ***
      modeSelect: ['FSK'], // Initially select no specific modes (shows all)
      modeItems: ['LoRa', 'FSK'], // Available mode options

      origData: [] // Holds the original data from the API
    }
  },
  beforeMount() {
    this.getSatellites();
  },
  methods: {
    async getSatellites() {
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/v1/satellites`);
        console.log("Satellites data received:", data);
        // Ensure data is an array before assigning
        this.origData = Array.isArray(data) ? data : [];
      } catch (error) {
          console.error("Error fetching satellites:", error);
          this.origData = []; // Set to empty array on error
          // Optionally show an error message to the user
      }
    },
    formatLaunchDate(date){
      // Add a check if the date is valid before formatting
      return date ? moment(date).format('MMMM Do YYYY, H:mm:ss') : 'N/A';
    },
  },
  computed:{
    satellites(){
      // Return empty array immediately if origData is not ready or empty
      if (!this.origData || this.origData.length === 0) {
        return [];
      }

      // Start with a copy of the original data
      let sats = [...this.origData];

      // --- Text Filter ---
      if (this.findTxt) {
        const searchText = this.findTxt.toLowerCase().trim();
        if (searchText) { // Only filter if text is not empty after trimming
          sats = sats.filter(s =>
            (s.name && s.name.toLowerCase().includes(searchText)) ||
            (s.displayName && s.displayName.toLowerCase().includes(searchText)) ||
            (s.description && s.description.toLowerCase().includes(searchText)) || // Added description
            (s.longDescription && s.longDescription.toLowerCase().includes(searchText))
          );
        }
      }

      // --- Status Filter ---
      if (this.statusSelect.length > 0) {
        sats = sats.filter(sat => sat.status && this.statusSelect.includes(sat.status));
      }

      // --- Frequency Filter (Improved Logic) ---
      if (this.freqSelect.length > 0) {
        sats = sats.filter(sat => {
          // Ensure configurations is an array
          if (!Array.isArray(sat.configurations)) return false;
          // Check if satellite has ANY configuration matching ANY selected frequency band
          return this.freqSelect.some(selectedFreqBand => {
             let freq_min, freq_max;
             // Determine range based on the selected band string
             if (selectedFreqBand === '137') { freq_min = 130; freq_max = 300; }
             else if (selectedFreqBand === '433') { freq_min = 400; freq_max = 550; }
             else if (selectedFreqBand === '868-915') { freq_min = 800; freq_max = 950; }
             else if (selectedFreqBand === '2400') { freq_min = 2100; freq_max = 2500; }
             else { return false; } // Unknown frequency band selected

             // Check if ANY configuration's frequency falls within this band's range
             return sat.configurations.some(conf => conf.freq && conf.freq > freq_min && conf.freq < freq_max);
          });
        });
      }

      // --- *** NEW Mode Filter *** ---
      if (this.modeSelect.length > 0) {
        sats = sats.filter(sat => {
          // Ensure configurations is an array
          if (!Array.isArray(sat.configurations)) return false;
          // Check if ANY configuration has a mode that is included in the selected modes
          return sat.configurations.some(conf => conf.mode && this.modeSelect.includes(conf.mode));
        });
      }

      // Return the final filtered array
      return sats;
    }
  }
}
</script>

<style>
.subheading {
  margin: 10px 20px;
}

.Supported {
  background: #4CAF50 !important;
}

.Future {
  background: #82B1FF  !important;
}


.Experimental {
  background: #FFC107  !important;
}

.Inactive {
  background: #FF5252  !important;
}

.clickable:hover {
  top: -5px;
}

</style>